/**
 * GravBase — Logo Designer V2 (Fabric.js Full Editor)
 * File: assets/section-anj-logo-designer-v2.js
 *
 * Features:
 *  - Gradient fills, image upload, curved text, snap-to-grid
 *  - Lock/hide layers panel, export resolution selector
 *  - Zoom in/out/reset, multi-select & group/ungroup
 *  - Copy/paste style, keyboard nudge, canvas size presets
 *  - Undo/redo (40 steps), AI refine via Worker
 */

(function () {
  'use strict';

  const FABRIC_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.1/fabric.min.js';
  const JSPDF_CDN  = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  const WORKER_URL = (window.anjLogoConfig?.workerUrl || '').replace(/\/$/, '');
  const SNAP_SIZE  = 10;

  /* ------------------------------------------------------------------
     Templates
     ------------------------------------------------------------------ */

  const TEMPLATES = {
    trucking: {
      label: 'Trucking / Fleet',
      svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 200"><rect width="500" height="200" fill="#293030"/><path d="M18,83 L80,83 L80,63 L126,100 L80,137 L80,117 L18,117 Z" fill="#FF76B4"/><rect x="142" y="62" width="3" height="76" fill="#FF76B4"/><text x="156" y="97" font-family="Arial Black,Impact" font-size="38" font-weight="900" fill="#ffffff">MOMENTUM</text><text x="158" y="122" font-family="Arial" font-size="11" fill="#FF76B4" letter-spacing="4">TRUCKING &amp; LOGISTICS</text></svg>`,
    },
    construction: {
      label: 'Construction',
      svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 200"><rect width="500" height="200" fill="#f4f4f4"/><rect x="18" y="54" width="84" height="17" fill="#e07b10"/><rect x="51" y="71" width="18" height="54" fill="#e07b10"/><rect x="18" y="125" width="84" height="17" fill="#e07b10"/><text x="130" y="102" font-family="Arial Black,Impact" font-size="38" font-weight="900" fill="#293030">FOUNDATION</text><text x="132" y="126" font-family="Arial" font-size="11" fill="#e07b10" letter-spacing="4">CONSTRUCTION CO.</text></svg>`,
    },
    retail: {
      label: 'Retail',
      svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 200"><rect width="500" height="200" fill="#ffffff"/><rect x="32" y="66" width="436" height="3" fill="#FF76B4"/><circle cx="32" cy="67" r="6" fill="#FF76B4"/><text x="32" y="117" font-family="Arial Black,Impact" font-size="48" font-weight="900" fill="#293030">MERIDIAN</text><rect x="32" y="130" width="436" height="3" fill="#FF76B4"/><text x="32" y="154" font-family="Arial" font-size="11" fill="#FF76B4" letter-spacing="5">RETAIL &amp; BRAND SOLUTIONS</text></svg>`,
    },
    food: {
      label: 'Food & Bev',
      svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 200"><rect width="500" height="200" fill="#1e1408"/><rect x="20" y="76" width="16" height="88" fill="#d4a853"/><rect x="40" y="55" width="16" height="109" fill="#d4a853"/><rect x="60" y="65" width="16" height="99" fill="#d4a853"/><circle cx="48" cy="50" r="9" fill="#d4a853"/><rect x="16" y="164" width="64" height="5" fill="#d4a853"/><text x="112" y="99" font-family="Arial Black,Impact" font-size="38" font-weight="900" fill="#ffffff">GROVE</text><text x="114" y="124" font-family="Arial" font-size="11" fill="#d4a853" letter-spacing="4">COFFEE &amp; PROVISIONS</text></svg>`,
    },
    automotive: {
      label: 'Automotive',
      svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 200"><rect width="500" height="200" fill="#141414"/><path d="M15,46 L125,46 L125,112 L70,158 L15,112 Z" fill="#e63946"/><path d="M24,55 L116,55 L116,108 L70,148 L24,108 Z" fill="#141414"/><rect x="34" y="80" width="72" height="5" fill="#e63946"/><rect x="34" y="91" width="54" height="5" fill="#e63946"/><rect x="34" y="102" width="36" height="5" fill="#e63946"/><text x="142" y="97" font-family="Arial Black,Impact" font-size="38" font-weight="900" fill="#ffffff">VECTOR</text><text x="144" y="122" font-family="Arial" font-size="11" fill="#e63946" letter-spacing="4">AUTO PERFORMANCE</text></svg>`,
    },
    realestate: {
      label: 'Real Estate',
      svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 200"><rect width="500" height="200" fill="#EFEFEA"/><path d="M70,38 L128,148 L12,148 Z" fill="#FF76B4"/><path d="M70,68 L106,138 L34,138 Z" fill="#EFEFEA"/><rect x="8" y="146" width="124" height="5" fill="#FF76B4"/><text x="158" y="102" font-family="Arial Black,Impact" font-size="38" font-weight="900" fill="#293030">APEX</text><text x="160" y="126" font-family="Arial" font-size="11" fill="#FF76B4" letter-spacing="4">REAL ESTATE GROUP</text></svg>`,
    },
  };

  /* ------------------------------------------------------------------
     State
     ------------------------------------------------------------------ */

  let naturalW = 600;
  let naturalH = 400;
  let canvas        = null;
  let undoStack     = [];
  let redoStack     = [];
  let isUpdating    = false;
  let hasDownloaded = false;
  let quotePulsed   = false;
  let aiHistory     = [];
  let copiedStyle   = null;
  let currentZoom   = 1;
  let snapEnabled   = true;

  /* ------------------------------------------------------------------
     Refs
     ------------------------------------------------------------------ */

  const $ = (id) => document.getElementById(id);

  const el = {
    // Re-captured in boot() after Fabric loads:
    templateGrid:    null,
    blankBtn:        null,
    canvasContainer: null,
    canvasOuter:     null,
    layersList:      null,
    // Stable refs captured once on load:
    zoomLabel:       $('ld-zoom-label'),
    snapToggle:      $('ld-snap'),
    bgBtns:          document.querySelectorAll('.anj-ld__bg-btn'),
    presetBtns:      document.querySelectorAll('.anj-ld__preset-btn'),
    toolBtns:        document.querySelectorAll('.anj-ld__tool-btn[data-tool]'),
    shapeBtns:       document.querySelectorAll('.anj-ld__tool-btn[data-shape]'),
    undoBtn:         $('ld-undo'),
    redoBtn:         $('ld-redo'),
    deleteBtn:       $('ld-delete'),
    groupBtn:        $('ld-group'),
    ungroupBtn:      $('ld-ungroup'),
    zoomInBtn:       $('ld-zoom-in'),
    zoomOutBtn:      $('ld-zoom-out'),
    zoomResetBtn:    $('ld-zoom-reset'),
    imgUpload:       $('ld-img-upload'),
    propsEmpty:      $('ld-props-empty'),
    propFill:        $('ld-prop-fill'),
    propText:        $('ld-prop-text'),
    propLayers:      $('ld-prop-layers'),
    fillTabs:        document.querySelectorAll('.anj-ld__fill-tab'),
    fillSolid:       $('ld-fill-solid'),
    fillGradient:    $('ld-fill-gradient'),
    fillPreview:     $('ld-fill-preview'),
    fillColor:       $('ld-fill-color'),
    gradStart:       $('ld-grad-start'),
    gradEnd:         $('ld-grad-end'),
    gradStartPrev:   $('ld-grad-start-preview'),
    gradEndPrev:     $('ld-grad-end-preview'),
    gradAngle:       $('ld-grad-angle'),
    gradAngleVal:    $('ld-grad-angle-val'),
    strokePreview:   $('ld-stroke-preview'),
    strokeColor:     $('ld-stroke-color'),
    strokeWidth:     $('ld-stroke-width'),
    strokeWidthVal:  $('ld-stroke-width-val'),
    opacity:         $('ld-opacity'),
    opacityVal:      $('ld-opacity-val'),
    fontFamily:      $('ld-font-family'),
    fontSize:        $('ld-font-size'),
    letterSpacing:   $('ld-letter-spacing'),
    boldBtn:         $('ld-bold'),
    italicBtn:       $('ld-italic'),
    underlineBtn:    $('ld-underline'),
    alignBtns:       document.querySelectorAll('.anj-ld__fmt-btn[data-align]'),
    curveEnable:     $('ld-curve-enable'),
    curveControls:   $('ld-curve-controls'),
    curveRadius:     $('ld-curve-radius'),
    curveRadiusVal:  $('ld-curve-radius-val'),
    copyStyleText:   $('ld-copy-style'),
    pasteStyleText:  $('ld-paste-style'),
    copyStyleShape:  $('ld-copy-style-shape'),
    pasteStyleShape: $('ld-paste-style-shape'),
    bringFront:      $('ld-bring-front'),
    sendBack:        $('ld-send-back'),
    duplicate:       $('ld-duplicate'),
    lockObj:         $('ld-lock-obj'),
    aiPrompt:        $('ld-ai-prompt'),
    aiApply:         $('ld-ai-apply'),
    exportRes:       $('ld-export-res'),
    dlSvg:           $('ld-dl-svg'),
    dlPng:           $('ld-dl-png'),
    dlPdf:           $('ld-dl-pdf'),
    quoteCta:        $('ld-quote-cta'),
  };

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function loadScript(src, cb) {
    if (window._ldScripts?.[src]) { cb(); return; }
    window._ldScripts = window._ldScripts || {};
    window._ldScripts[src] = true;
    const s = document.createElement('script');
    s.src = src; s.onload = cb;
    s.onerror = () => console.error('[ANJ Logo] Failed to load', src);
    document.head.appendChild(s);
  }

  function boot() {
    loadScript(FABRIC_CDN, () => {
      // Re-capture all element refs after Fabric has loaded and DOM is settled
      el.templateGrid    = $('ld-template-grid');
      el.blankBtn        = $('ld-blank');
      el.canvasEl        = $('ld-canvas');
      el.canvasContainer = $('ld-canvas-container');
      el.canvasOuter     = $('ld-canvas-outer');
      el.layersList      = $('ld-layers-list');

      initCanvas(600, 400, '');
      buildTemplatePicker();
      bindEvents();
    });
  }

  /* ------------------------------------------------------------------
     Canvas init
     ------------------------------------------------------------------ */

  function initCanvas(w, h, bg) {
    naturalW = w || 600;
    naturalH = h || 400;

    const isGrid = bg === '' || bg === 'grid';

    if (canvas) {
      canvas.clear();
      canvas.setWidth(naturalW);
      canvas.setHeight(naturalH);
      canvas.backgroundColor = isGrid ? '' : (bg || '');
      canvas.renderAll();
      undoStack = []; redoStack = [];
      saveHistory();
      // Re-apply grid class state
      if (el.canvasContainer) {
        el.canvasContainer.classList.toggle('is-grid', isGrid);
      }
      fitCanvas();
      return;
    }

    // First init only
    canvas = new fabric.Canvas('ld-canvas', {
      width:                  naturalW,
      height:                 naturalH,
      backgroundColor:        bg || '',
      selection:              true,
      preserveObjectStacking: true,
      fireRightClick:         true,
    });

    // Snap to grid
    canvas.on('object:moving', (e) => {
      if (!snapEnabled) return;
      const obj = e.target;
      obj.set({
        left: Math.round(obj.left / SNAP_SIZE) * SNAP_SIZE,
        top:  Math.round(obj.top  / SNAP_SIZE) * SNAP_SIZE,
      });
    });

    canvas.on('selection:created', updateProps);
    canvas.on('selection:updated', updateProps);
    canvas.on('selection:cleared', clearProps);
    canvas.on('object:modified',   () => { saveHistory(); refreshLayers(); });
    canvas.on('object:added',      () => { saveHistory(); refreshLayers(); });
    canvas.on('object:removed',    () => { saveHistory(); refreshLayers(); });

    saveHistory();
    initResizeObserver();
    // Ensure grid class is active on load
    if (el.canvasContainer) el.canvasContainer.classList.add('is-grid');
    requestAnimationFrame(() => requestAnimationFrame(fitCanvas));
  }

  function fitCanvas() {
    if (!canvas || !el.canvasOuter) return;
    const containerW = el.canvasOuter.clientWidth - 16;
    if (containerW <= 0) return;
    const scale = containerW / naturalW;
    canvas.setZoom(scale);
    canvas.setWidth(naturalW * scale);
    canvas.setHeight(naturalH * scale);
    currentZoom = scale;
    updateZoomLabel();
  }

  function initResizeObserver() {
    if (!el.canvasOuter) return;
    const ro = new ResizeObserver(() => fitCanvas());
    ro.observe(el.canvasOuter);
  }

  /* ------------------------------------------------------------------
     Zoom
     ------------------------------------------------------------------ */

  function zoom(factor) {
    if (!canvas) return;
    const newScale = Math.min(4, Math.max(0.25, currentZoom * factor));
    canvas.setZoom(newScale);
    canvas.setWidth(naturalW * newScale);
    canvas.setHeight(naturalH * newScale);
    currentZoom = newScale;
    updateZoomLabel();
  }

  function resetZoom() { fitCanvas(); }

  function updateZoomLabel() {
    if (el.zoomLabel) el.zoomLabel.textContent = `${Math.round(currentZoom * 100)}%`;
  }

  /* ------------------------------------------------------------------
     History
     ------------------------------------------------------------------ */

  function saveHistory() {
    if (isUpdating || !canvas) return;
    undoStack.push(JSON.stringify(canvas.toJSON(['lockMovementX','lockMovementY','evented','selectable','visible','data'])));
    if (undoStack.length > 40) undoStack.shift();
    redoStack = [];
  }

  function undo() {
    if (undoStack.length < 2 || !canvas) return;
    redoStack.push(undoStack.pop());
    isUpdating = true;
    canvas.loadFromJSON(undoStack[undoStack.length - 1], () => {
      canvas.renderAll(); isUpdating = false; refreshLayers();
    });
  }

  function redo() {
    if (!redoStack.length || !canvas) return;
    const state = redoStack.pop();
    undoStack.push(state);
    isUpdating = true;
    canvas.loadFromJSON(state, () => {
      canvas.renderAll(); isUpdating = false; refreshLayers();
    });
  }

  /* ------------------------------------------------------------------
     Layers panel
     ------------------------------------------------------------------ */

  function refreshLayers() {
    if (!el.layersList || !canvas) return;
    const objects = canvas.getObjects();
    el.layersList.innerHTML = '';

    // Render top-to-bottom (reverse stack order)
    [...objects].reverse().forEach((obj) => {
      const item = document.createElement('div');
      item.className = 'anj-ld__layer-item';
      if (!obj.visible)  item.classList.add('is-hidden');
      if (obj.lockMovementX) item.classList.add('is-locked');
      if (canvas.getActiveObject() === obj) item.classList.add('is-active');

      const type = obj.type === 'i-text' || obj.type === 'text' ? 'T' :
                   obj.type === 'image'   ? '🖼' :
                   obj.type === 'group'   ? '⬡' : '◼';

      const name = obj.data?.name || (obj.type === 'i-text' ? obj.text?.slice(0,12) : obj.type) || 'Object';

      item.innerHTML = `
        <span class="anj-ld__layer-icon" style="font-size:11px">${type}</span>
        <span class="anj-ld__layer-name">${name}</span>
        <button class="anj-ld__layer-vis" title="${obj.visible ? 'Hide' : 'Show'}">
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            ${obj.visible
              ? '<path d="M1 6.5C2.5 3.5 5 2 6.5 2S10.5 3.5 12 6.5C10.5 9.5 8 11 6.5 11S2.5 9.5 1 6.5z" stroke="currentColor" stroke-width="1.2"/><circle cx="6.5" cy="6.5" r="1.8" stroke="currentColor" stroke-width="1.2"/>'
              : '<path d="M2 2l9 9M1 6.5C2.5 3.5 5 2 6.5 2c.7 0 1.4.2 2 .5M12 6.5c-.5.9-1.2 1.7-2 2.2M5 10.8C5.4 11 5.9 11.1 6.5 11c1.5 0 4-1.5 5.5-4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>'}
          </svg>
        </button>
      `;

      item.addEventListener('click', (e) => {
        if (e.target.closest('.anj-ld__layer-vis')) return;
        canvas.setActiveObject(obj);
        canvas.renderAll();
        updateProps();
        refreshLayers();
      });

      item.querySelector('.anj-ld__layer-vis')?.addEventListener('click', (e) => {
        e.stopPropagation();
        obj.visible = !obj.visible;
        canvas.renderAll();
        refreshLayers();
      });

      el.layersList.appendChild(item);
    });
  }

  /* ------------------------------------------------------------------
     Templates
     ------------------------------------------------------------------ */

  function buildTemplatePicker() {
    if (!el.templateGrid) return;
    el.templateGrid.innerHTML = '';
    Object.entries(TEMPLATES).forEach(([id, tpl]) => {
      const card = document.createElement('button');
      card.className = 'anj-ld__tpl-card'; card.type = 'button'; card.dataset.id = id;
      card.innerHTML = `<div class="anj-ld__tpl-thumb">${tpl.svg}</div><span class="anj-ld__tpl-label">${tpl.label}</span>`;
      card.addEventListener('click', () => loadTemplate(id));
      el.templateGrid.appendChild(card);
    });
  }

  function loadTemplate(id) {
    const tpl = TEMPLATES[id]; if (!tpl || !canvas) return;
    el.templateGrid?.querySelectorAll('.anj-ld__tpl-card').forEach((c) => c.classList.toggle('is-active', c.dataset.id === id));
    fabric.loadSVGFromString(tpl.svg, (objects, options) => {
      canvas.clear();
      const group = fabric.util.groupSVGElements(objects, options);
      group.scaleToWidth(naturalW);
      group.set({ left: 0, top: 0 });
      canvas.add(group); canvas.renderAll(); saveHistory(); refreshLayers();
    });
  }

  /* ------------------------------------------------------------------
     Canvas presets
     ------------------------------------------------------------------ */

  function resizeCanvas(w, h) {
    naturalW = w;
    naturalH = h;
    const isGrid = el.canvasContainer?.classList.contains('is-grid');
    initCanvas(w, h, isGrid ? '' : (canvas?.backgroundColor || ''));
  }

  /* ------------------------------------------------------------------
     Tools
     ------------------------------------------------------------------ */

  function setActiveTool(name) {
    el.toolBtns?.forEach((b) => b.classList.toggle('is-active', b.dataset.tool === name));
    if (!canvas) return;
    canvas.isDrawingMode = (name === 'draw');
    if (name === 'draw') {
      canvas.freeDrawingBrush.width = 3;
      canvas.freeDrawingBrush.color = '#FF76B4';
    }
  }

  function addShape(type) {
    if (!canvas) return;
    const fill = '#FF76B4';
    const common = { left: 80, top: 60, fill, stroke: 'transparent', strokeWidth: 0 };
    let obj;
    switch (type) {
      case 'rect':     obj = new fabric.Rect({ ...common, width: 120, height: 80, rx: 4 }); break;
      case 'circle':   obj = new fabric.Circle({ ...common, radius: 50 }); break;
      case 'triangle': obj = new fabric.Triangle({ ...common, width: 100, height: 90 }); break;
      case 'line':     obj = new fabric.Line([0,0,140,0], { left:80, top:100, stroke:'#FF76B4', strokeWidth:3, strokeLineCap:'round' }); break;
      case 'arrow': {
        obj = new fabric.Path('M0 0 L120 0 L100 -14 M120 0 L100 14', {
          left:80, top:100, stroke:'#FF76B4', strokeWidth:3, fill:'transparent', strokeLineCap:'round', strokeLineJoin:'round',
        }); break;
      }
      case 'diamond': {
        obj = new fabric.Polygon([{x:50,y:0},{x:100,y:50},{x:50,y:100},{x:0,y:50}], { ...common, left:80, top:40 }); break;
      }
      case 'star': {
        const pts = []; const n=5, r1=50, r2=22;
        for (let i=0; i<n*2; i++) { const a=(Math.PI/n)*i-Math.PI/2, r=i%2===0?r1:r2; pts.push({x:Math.cos(a)*r, y:Math.sin(a)*r}); }
        obj = new fabric.Polygon(pts, { ...common, left:80, top:40 }); break;
      }
      case 'hexagon': {
        const hex=[];
        for (let i=0;i<6;i++) { const a=(Math.PI/3)*i-Math.PI/6; hex.push({x:Math.cos(a)*50, y:Math.sin(a)*50}); }
        obj = new fabric.Polygon(hex, { ...common, left:80, top:40 }); break;
      }
    }
    if (obj) { obj.data = { name: type }; canvas.add(obj); canvas.setActiveObject(obj); canvas.renderAll(); }
    setActiveTool('select');
  }

  function addText() {
    if (!canvas) return;
    const txt = new fabric.IText('Your Text', {
      left:100, top:80, fontFamily:'Arial Black', fontSize:36, fill:'#ffffff', fontWeight:'bold',
    });
    txt.data = { name: 'Text' };
    canvas.add(txt); canvas.setActiveObject(txt); txt.enterEditing(); canvas.renderAll();
    setActiveTool('select');
  }

  function addImage(file) {
    if (!canvas || !file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      fabric.Image.fromURL(e.target.result, (img) => {
        img.scaleToWidth(Math.min(200, naturalW * 0.4));
        img.set({ left: 60, top: 40 });
        img.data = { name: 'Image' };
        canvas.add(img);
        canvas.discardActiveObject();
        canvas.renderAll();
        // Defer selection so mouse-up from file picker doesn't trigger a drag
        requestAnimationFrame(() => {
          canvas.setActiveObject(img);
          canvas.renderAll();
        });
      });
    };
    reader.readAsDataURL(file);
  }

  /* ------------------------------------------------------------------
     Curved text
     ------------------------------------------------------------------ */

  function applyCurvedText(radius) {
    const obj = canvas?.getActiveObject();
    if (!obj || (obj.type !== 'i-text' && obj.type !== 'text')) return;

    const text   = obj.text || 'TEXT';
    const font   = obj.fontFamily || 'Arial Black';
    const size   = obj.fontSize   || 36;
    const fill   = obj.fill       || '#ffffff';
    const left   = obj.left       || 100;
    const top    = obj.top        || 80;

    // Build curved text using arc path approximation with individual chars
    const chars  = text.split('');
    const total  = chars.length;
    const rPx    = radius;
    const step   = (2 * Math.PI) / (total * 2.4);
    const startA = -Math.PI / 2 - (step * (total - 1)) / 2;

    const group = new fabric.Group([], {
      left, top, selectable: true,
    });

    chars.forEach((ch, i) => {
      const angle = startA + step * i;
      const x     = Math.cos(angle) * rPx;
      const y     = Math.sin(angle) * rPx;
      const t = new fabric.Text(ch, {
        left: x, top: y, originX: 'center', originY: 'bottom',
        fontFamily: font, fontSize: size, fill,
        angle: (angle + Math.PI / 2) * (180 / Math.PI),
      });
      group.addWithUpdate(t);
    });

    group.data = { name: 'Curved Text', curved: true };
    canvas.remove(obj);
    canvas.add(group);
    canvas.setActiveObject(group);
    canvas.renderAll();
    saveHistory();
    refreshLayers();
  }

  /* ------------------------------------------------------------------
     Gradient
     ------------------------------------------------------------------ */

  function applyGradient() {
    const obj = canvas?.getActiveObject(); if (!obj) return;
    const start = el.gradStart?.value || '#FF76B4';
    const end   = el.gradEnd?.value   || '#B23A6F';
    const angle = parseInt(el.gradAngle?.value || '90');
    const rad   = angle * (Math.PI / 180);

    const w = obj.width  || 100;
    const h = obj.height || 100;
    const x1 = Math.cos(rad + Math.PI) * w / 2 + w / 2;
    const y1 = Math.sin(rad + Math.PI) * h / 2 + h / 2;
    const x2 = Math.cos(rad)           * w / 2 + w / 2;
    const y2 = Math.sin(rad)           * h / 2 + h / 2;

    obj.set('fill', new fabric.Gradient({
      type: 'linear',
      coords: { x1, y1, x2, y2 },
      colorStops: [{ offset: 0, color: start }, { offset: 1, color: end }],
    }));
    canvas.renderAll();
    if (el.gradStartPrev) el.gradStartPrev.style.background = start;
    if (el.gradEndPrev)   el.gradEndPrev.style.background   = end;
  }

  /* ------------------------------------------------------------------
     Style copy/paste
     ------------------------------------------------------------------ */

  function copyStyle() {
    const obj = canvas?.getActiveObject(); if (!obj) return;
    copiedStyle = {
      fill:        obj.fill,
      stroke:      obj.stroke,
      strokeWidth: obj.strokeWidth,
      opacity:     obj.opacity,
      fontFamily:  obj.fontFamily,
      fontSize:    obj.fontSize,
      fontWeight:  obj.fontWeight,
      fontStyle:   obj.fontStyle,
      textDecoration: obj.textDecoration,
      charSpacing: obj.charSpacing,
    };
  }

  function pasteStyle() {
    if (!copiedStyle) return;
    const obj = canvas?.getActiveObject(); if (!obj) return;
    Object.entries(copiedStyle).forEach(([k, v]) => { if (v !== undefined) obj.set(k, v); });
    canvas.renderAll();
    updateProps();
  }

  /* ------------------------------------------------------------------
     Properties panel
     ------------------------------------------------------------------ */

  function updateProps() {
    const obj = canvas?.getActiveObject(); if (!obj) { clearProps(); return; }

    const isText = obj.type === 'i-text' || obj.type === 'text';
    if (el.propsEmpty)  el.propsEmpty.style.display = 'none';
    if (el.propFill)    el.propFill.hidden    = false;
    if (el.propText)    el.propText.hidden    = !isText;
    if (el.propLayers)  el.propLayers.hidden  = false;

    isUpdating = true;

    // Fill
    const fill = obj.fill || 'transparent';
    const isGrad = fill && typeof fill === 'object';
    if (!isGrad && fill !== 'transparent') {
      setFillTab('solid');
      if (el.fillColor)   el.fillColor.value = toHex(fill);
      if (el.fillPreview) el.fillPreview.style.background = fill;
    } else if (isGrad) {
      setFillTab('gradient');
    } else {
      setFillTab('none');
    }

    // Stroke
    const stroke = obj.stroke || '#FF76B4';
    if (el.strokePreview) el.strokePreview.style.background = typeof stroke === 'string' ? stroke : '#FF76B4';
    if (el.strokeColor && typeof stroke === 'string') el.strokeColor.value = toHex(stroke);
    if (el.strokeWidth)    el.strokeWidth.value    = obj.strokeWidth || 0;
    if (el.strokeWidthVal) el.strokeWidthVal.textContent = obj.strokeWidth || 0;

    // Opacity
    const op = Math.round((obj.opacity ?? 1) * 100);
    if (el.opacity)    el.opacity.value           = op;
    if (el.opacityVal) el.opacityVal.textContent  = `${op}%`;

    // Text
    if (isText) {
      if (el.fontFamily)   el.fontFamily.value    = obj.fontFamily   || 'Arial Black';
      if (el.fontSize)     el.fontSize.value      = Math.round(obj.fontSize || 36);
      if (el.letterSpacing) el.letterSpacing.value = obj.charSpacing  || 0;
      const isBold = obj.fontWeight === 'bold' || parseInt(obj.fontWeight) >= 700;
      const isItal = obj.fontStyle === 'italic';
      const isUnder = obj.underline;
      el.boldBtn?.classList.toggle('is-active', isBold);
      el.italicBtn?.classList.toggle('is-active', isItal);
      el.underlineBtn?.classList.toggle('is-active', !!isUnder);
      el.alignBtns?.forEach((b) => b.classList.toggle('is-active', b.dataset.align === (obj.textAlign || 'left')));
    }

    isUpdating = false;
  }

  function setFillTab(mode) {
    el.fillTabs?.forEach((t) => t.classList.toggle('is-active', t.dataset.fill === mode));
    if (el.fillSolid)    el.fillSolid.hidden    = mode !== 'solid';
    if (el.fillGradient) el.fillGradient.hidden = mode !== 'gradient';
  }

  function clearProps() {
    if (el.propsEmpty)  el.propsEmpty.style.display = '';
    if (el.propFill)    el.propFill.hidden    = true;
    if (el.propText)    el.propText.hidden    = true;
    if (el.propLayers)  el.propLayers.hidden  = true;
  }

  function applyProp(cb) {
    if (isUpdating || !canvas) return;
    const obj = canvas.getActiveObject(); if (!obj) return;
    cb(obj); canvas.renderAll();
  }

  function toHex(color) {
    if (!color || typeof color !== 'string') return '#000000';
    if (color.startsWith('#') && color.length === 7) return color;
    if (color.startsWith('#') && color.length === 4) return '#'+color[1]+color[1]+color[2]+color[2]+color[3]+color[3];
    const d = document.createElement('div'); d.style.color = color;
    document.body.appendChild(d);
    const c = getComputedStyle(d).color; document.body.removeChild(d);
    const m = c.match(/(\d+),\s*(\d+),\s*(\d+)/);
    if (m) return '#'+[m[1],m[2],m[3]].map((n)=>parseInt(n).toString(16).padStart(2,'0')).join('');
    return '#000000';
  }

  /* ------------------------------------------------------------------
     AI Refine
     ------------------------------------------------------------------ */

  async function handleAiRefine() {
    const prompt = el.aiPrompt?.value.trim(); if (!prompt) return;
    if (!WORKER_URL) { alert('Worker URL not configured.'); return; }
    if (!canvas) return;
    const svg = canvas.toSVG();
    if (el.aiApply) { el.aiApply.disabled = true; el.aiApply.textContent = 'Working…'; }
    try {
      const res  = await fetch(`${WORKER_URL}/refine`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ prompt, svg, history:aiHistory }) });
      if (!res.ok) throw new Error(`Server ${res.status}`);
      const data = await res.json();
      if (!data.svg) throw new Error('No SVG.');
      aiHistory.push({ role:'user', content:prompt }, { role:'assistant', content:data.svg });
      if (aiHistory.length > 20) aiHistory = aiHistory.slice(-20);
      fabric.loadSVGFromString(data.svg, (objects, options) => {
        const bg = canvas.backgroundColor; canvas.clear(); canvas.backgroundColor = bg;
        const group = fabric.util.groupSVGElements(objects, options);
        group.scaleToWidth(naturalW);
        group.set({ left:0, top:0 }); canvas.add(group); canvas.renderAll(); saveHistory(); refreshLayers();
      });
      if (el.aiPrompt) el.aiPrompt.value = '';
    } catch (err) {
      console.error('[ANJ Logo] AI error:', err); alert('AI refinement failed.');
    } finally {
      if (el.aiApply) { el.aiApply.disabled = false; el.aiApply.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1l1.1 2.6L10 5l-2.9 1.4.9 3L6 8 3.9 9.4l.9-3L2 5l2.9-1.4z" fill="currentColor"/></svg> Apply with AI'; }
    }
  }

  /* ------------------------------------------------------------------
     Downloads
     ------------------------------------------------------------------ */

  function getMultiplier() { return parseInt(el.exportRes?.value || '3'); }

  function afterDownload() {
    if (!hasDownloaded) {
      hasDownloaded = true;
      if (!quotePulsed && el.quoteCta) {
        quotePulsed = true;
        el.quoteCta.classList.add('pulse-glow');
        el.quoteCta.addEventListener('animationend', () => el.quoteCta.classList.remove('pulse-glow'), { once:true });
      }
    }
  }

  function triggerDownload(url, name) {
    const a = document.createElement('a'); a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }

  function downloadSvg() {
    if (!canvas) return;
    const blob = new Blob([canvas.toSVG()], { type:'image/svg+xml;charset=utf-8' });
    triggerDownload(URL.createObjectURL(blob), 'anj-logo.svg');
    afterDownload();
  }

  function downloadPng() {
    if (!canvas) return;
    const dataUrl = canvas.toDataURL({ format:'png', multiplier: getMultiplier() / currentZoom });
    triggerDownload(dataUrl, 'anj-logo.png');
    afterDownload();
  }

  function downloadPdf() {
    if (!canvas) return;
    loadScript(JSPDF_CDN, () => {
      const { jsPDF } = window.jspdf;
      const w = canvas.getWidth() / currentZoom, h = canvas.getHeight() / currentZoom;
      const mmW = w * 0.264583, mmH = h * 0.264583;
      const doc = new jsPDF({ unit:'mm', format:[mmW,mmH], orientation:mmW>mmH?'l':'p' });
      doc.addImage(canvas.toDataURL({ format:'png', multiplier: getMultiplier() / currentZoom }), 'PNG', 0, 0, mmW, mmH);
      doc.save('anj-logo.pdf');
      afterDownload();
    });
  }

  /* ------------------------------------------------------------------
     Events
     ------------------------------------------------------------------ */

  function bindEvents() {

    // Blank canvas
    el.blankBtn?.addEventListener('click', () => {
      el.templateGrid?.querySelectorAll('.anj-ld__tpl-card').forEach((c) => c.classList.remove('is-active'));
      const bg = canvas?.backgroundColor || '#293030';
      initCanvas(naturalW, naturalH, bg);
    });

    // Canvas presets
    el.presetBtns?.forEach((btn) => {
      btn.addEventListener('click', () => {
        el.presetBtns.forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        resizeCanvas(parseInt(btn.dataset.w), parseInt(btn.dataset.h));
      });
    });

    // Snap toggle
    el.snapToggle?.addEventListener('change', () => { snapEnabled = el.snapToggle.checked; });

    // Background toggle
    el.bgBtns?.forEach((btn) => {
      btn.addEventListener('click', () => {
        el.bgBtns.forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        if (!canvas) return;
        const bg = btn.dataset.bg;
        if (bg === 'grid') {
          canvas.backgroundColor = '';
          el.canvasContainer?.classList.add('is-grid');
        } else {
          canvas.backgroundColor = bg;
          el.canvasContainer?.classList.remove('is-grid');
        }
        canvas.renderAll();
      });
    });

    // Tool buttons
    el.toolBtns?.forEach((btn) => {
      btn.addEventListener('click', () => {
        const tool = btn.dataset.tool;
        if (tool === 'text') { addText(); return; }
        setActiveTool(tool);
      });
    });

    // Shape buttons
    el.shapeBtns?.forEach((btn) => btn.addEventListener('click', () => addShape(btn.dataset.shape)));

    // Image upload
    el.imgUpload?.addEventListener('change', (e) => { addImage(e.target.files?.[0]); e.target.value = ''; });

    // Undo/redo/delete
    el.undoBtn?.addEventListener('click', undo);
    el.redoBtn?.addEventListener('click', redo);
    el.deleteBtn?.addEventListener('click', () => {
      const obj = canvas?.getActiveObject(); if (!obj) return;
      if (obj.type === 'activeSelection') { obj.getObjects().forEach((o) => canvas.remove(o)); canvas.discardActiveObject(); }
      else canvas.remove(obj);
      canvas.renderAll();
    });

    // Group / ungroup
    el.groupBtn?.addEventListener('click', () => {
      const sel = canvas?.getActiveObject();
      if (!sel || sel.type !== 'activeSelection') return;
      const group = sel.toGroup();
      group.data = { name: 'Group' };
      canvas.setActiveObject(group); canvas.renderAll(); saveHistory(); refreshLayers();
    });

    el.ungroupBtn?.addEventListener('click', () => {
      const sel = canvas?.getActiveObject();
      if (!sel || sel.type !== 'group') return;
      sel.toActiveSelection(); canvas.renderAll(); saveHistory(); refreshLayers();
    });

    // Zoom
    el.zoomInBtn?.addEventListener('click',    () => zoom(1.25));
    el.zoomOutBtn?.addEventListener('click',   () => zoom(0.8));
    el.zoomResetBtn?.addEventListener('click', resetZoom);

    // Mousewheel zoom
    el.canvasOuter?.addEventListener('wheel', (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      zoom(e.deltaY < 0 ? 1.1 : 0.9);
    }, { passive: false });

    // Fill tabs
    el.fillTabs?.forEach((tab) => {
      tab.addEventListener('click', () => {
        const mode = tab.dataset.fill;
        setFillTab(mode);
        if (mode === 'none') applyProp((obj) => obj.set('fill', 'transparent'));
        if (mode === 'gradient') applyGradient();
      });
    });

    // Fill solid
    el.fillColor?.addEventListener('input', () => {
      applyProp((obj) => { obj.set('fill', el.fillColor.value); if (el.fillPreview) el.fillPreview.style.background = el.fillColor.value; });
    });

    // Gradient
    const reGrad = () => applyGradient();
    el.gradStart?.addEventListener('input', () => { if (el.gradStartPrev) el.gradStartPrev.style.background = el.gradStart.value; reGrad(); });
    el.gradEnd?.addEventListener('input',   () => { if (el.gradEndPrev)   el.gradEndPrev.style.background   = el.gradEnd.value;   reGrad(); });
    el.gradAngle?.addEventListener('input', () => { if (el.gradAngleVal) el.gradAngleVal.textContent = `${el.gradAngle.value}°`; reGrad(); });

    // Stroke
    el.strokeColor?.addEventListener('input', () => {
      applyProp((obj) => { obj.set('stroke', el.strokeColor.value); if (el.strokePreview) el.strokePreview.style.background = el.strokeColor.value; });
    });
    el.strokeWidth?.addEventListener('input', () => {
      const v = parseInt(el.strokeWidth.value);
      applyProp((obj) => obj.set('strokeWidth', v));
      if (el.strokeWidthVal) el.strokeWidthVal.textContent = v;
    });

    // Opacity
    el.opacity?.addEventListener('input', () => {
      const v = parseInt(el.opacity.value) / 100;
      applyProp((obj) => obj.set('opacity', v));
      if (el.opacityVal) el.opacityVal.textContent = `${Math.round(v*100)}%`;
    });

    // Font
    el.fontFamily?.addEventListener('change', () => applyProp((obj) => obj.set('fontFamily', el.fontFamily.value)));
    el.fontSize?.addEventListener('input',    () => { const v=parseInt(el.fontSize.value); if(v>0) applyProp((obj)=>obj.set('fontSize',v)); });
    el.letterSpacing?.addEventListener('input',() => applyProp((obj)=>obj.set('charSpacing',parseInt(el.letterSpacing.value))));

    el.boldBtn?.addEventListener('click', () => {
      applyProp((obj) => { const b=obj.fontWeight==='bold'; obj.set('fontWeight',b?'normal':'bold'); el.boldBtn.classList.toggle('is-active',!b); });
    });
    el.italicBtn?.addEventListener('click', () => {
      applyProp((obj) => { const i=obj.fontStyle==='italic'; obj.set('fontStyle',i?'normal':'italic'); el.italicBtn.classList.toggle('is-active',!i); });
    });
    el.underlineBtn?.addEventListener('click', () => {
      applyProp((obj) => { const u=!!obj.underline; obj.set('underline',!u); el.underlineBtn.classList.toggle('is-active',!u); });
    });

    el.alignBtns?.forEach((btn) => {
      btn.addEventListener('click', () => {
        applyProp((obj) => obj.set('textAlign', btn.dataset.align));
        el.alignBtns.forEach((b) => b.classList.toggle('is-active', b===btn));
      });
    });

    // Curved text
    el.curveEnable?.addEventListener('change', () => {
      if (el.curveControls) el.curveControls.hidden = !el.curveEnable.checked;
      if (el.curveEnable.checked) applyCurvedText(parseInt(el.curveRadius?.value||150));
    });
    el.curveRadius?.addEventListener('input', () => {
      const v = parseInt(el.curveRadius.value);
      if (el.curveRadiusVal) el.curveRadiusVal.textContent = v;
      if (el.curveEnable?.checked) applyCurvedText(v);
    });

    // Style paste
    [el.copyStyleText, el.copyStyleShape].forEach((b) => b?.addEventListener('click', copyStyle));
    [el.pasteStyleText, el.pasteStyleShape].forEach((b) => b?.addEventListener('click', pasteStyle));

    // Layer ops
    el.bringFront?.addEventListener('click', () => applyProp((obj) => canvas.bringToFront(obj)));
    el.sendBack?.addEventListener('click',   () => applyProp((obj) => canvas.sendToBack(obj)));
    el.duplicate?.addEventListener('click',  () => {
      const obj = canvas?.getActiveObject(); if (!obj) return;
      obj.clone((c) => { c.set({ left:obj.left+20, top:obj.top+20 }); canvas.add(c); canvas.setActiveObject(c); canvas.renderAll(); });
    });
    el.lockObj?.addEventListener('click', () => {
      const obj = canvas?.getActiveObject(); if (!obj) return;
      const locked = obj.lockMovementX;
      obj.set({ lockMovementX:!locked, lockMovementY:!locked, lockScalingX:!locked, lockScalingY:!locked, lockRotation:!locked, evented:locked });
      el.lockObj.textContent = locked ? 'Lock' : 'Unlock';
      canvas.renderAll(); refreshLayers();
    });

    // AI
    el.aiApply?.addEventListener('click', handleAiRefine);
    el.aiPrompt?.addEventListener('keydown', (e) => { if (e.key==='Enter'&&!e.shiftKey) { e.preventDefault(); handleAiRefine(); } });

    // Downloads
    el.dlSvg?.addEventListener('click', downloadSvg);
    el.dlPng?.addEventListener('click', downloadPng);
    el.dlPdf?.addEventListener('click', downloadPdf);

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      const tag = e.target.tagName;
      if (tag==='INPUT'||tag==='TEXTAREA'||tag==='SELECT') return;

      // Delete
      if (e.key==='Delete'||e.key==='Backspace') {
        const obj=canvas?.getActiveObject(); if(!obj) return;
        if(obj.type==='activeSelection') { obj.getObjects().forEach((o)=>canvas.remove(o)); canvas.discardActiveObject(); }
        else canvas.remove(obj);
        canvas.renderAll();
      }

      // Undo/redo
      if ((e.ctrlKey||e.metaKey) && e.key==='z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey||e.metaKey) && e.key==='z' &&  e.shiftKey) { e.preventDefault(); redo(); }
      if ((e.ctrlKey||e.metaKey) && e.key==='y') { e.preventDefault(); redo(); }

      // Select all
      if ((e.ctrlKey||e.metaKey) && e.key==='a') {
        e.preventDefault();
        canvas?.setActiveObject(new fabric.ActiveSelection(canvas.getObjects(), { canvas }));
        canvas?.renderAll();
      }

      // Group
      if (e.key==='g' && !e.shiftKey && (e.ctrlKey||e.metaKey)) { e.preventDefault(); el.groupBtn?.click(); }
      if (e.key==='g' &&  e.shiftKey && (e.ctrlKey||e.metaKey)) { e.preventDefault(); el.ungroupBtn?.click(); }

      // Tool shortcuts
      if (!e.ctrlKey && !e.metaKey) {
        if (e.key==='v') setActiveTool('select');
        if (e.key==='d') setActiveTool('draw');
        if (e.key==='t') addText();
        if (e.key==='+' || e.key==='=') zoom(1.2);
        if (e.key==='-') zoom(0.8);
        if (e.key==='0') resetZoom();
      }

      // Nudge with arrow keys (1px normal, 10px with shift)
      const nudge = e.shiftKey ? 10 : 1;
      const obj   = canvas?.getActiveObject();
      if (!obj) return;
      if (e.key==='ArrowLeft')  { e.preventDefault(); obj.set('left', obj.left - nudge); canvas.renderAll(); }
      if (e.key==='ArrowRight') { e.preventDefault(); obj.set('left', obj.left + nudge); canvas.renderAll(); }
      if (e.key==='ArrowUp')    { e.preventDefault(); obj.set('top',  obj.top  - nudge); canvas.renderAll(); }
      if (e.key==='ArrowDown')  { e.preventDefault(); obj.set('top',  obj.top  + nudge); canvas.renderAll(); }
    });
  }

  /* ------------------------------------------------------------------
     Init
     ------------------------------------------------------------------ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
